import React, { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import { ISWCValidator } from '@App/helpers/validate'
import { IWorkDetailsValidations } from '@App/interfaces/validations'
import { DATE_REGEX } from '@Const/regexp'
import { getValueForm } from '@App/helpers/get-value-form'
import { workDetailsInvalidCheck } from '@App/helpers/work-entities/entities-validators'
import { akaTitleRemover } from '@App/helpers/work-entities/removers'
import { containsNewLine, workTitleInvalidCheck } from '@App/helpers/work-entities-fields-validators'
import { StoresContext } from '@App/contexts'
import { OptionModel } from '@Entities/lib'
import { WorkModel } from '@Entities/work/work'
import AddAlternativeTitlesSidebar from '@Components/Sidebars/AddAlternativeTitlesSidebar'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import AlternativeTitlesTableInfo from '@Scenes/WorkScenes/Steps/WorkDetailsStep/AlternativeTitlesTableInfo'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import { TOOLTIP_TEXT } from '@Const/tooltip-text'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { WorkDetailsModel } from '@Entities/work/work-details-model'
import { maxByteLength } from '@Entities/schemas/common/useful-zod-functions'
import WorkDetailsFields from '@Scenes/WorkScenes/Steps/WorkDetailsStep/WorkDetailsFields'

const WorkDetailsStep = () => {
    const { workStore, libStore } = useContext(StoresContext)

    const presetData = (): Partial<WorkDetailsModel> | undefined => {
        const workDraftFromLocal = workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return undefined
        }
        return {
            title: workDraftFromLocal.title,
            duration: workDraftFromLocal.duration,
            languageCode: workDraftFromLocal.languageCode,
            copyrightOfficeNumber: workDraftFromLocal.copyrightOfficeNumber,
            copyrightOfficeDate: workDraftFromLocal.copyrightOfficeDate,
            firstUseRefusalIndicator: workDraftFromLocal.firstUseRefusalIndicator,
            rightsholderProprietaryId: workDraftFromLocal.rightsholderProprietaryId,
            iswc: workDraftFromLocal.iswc,
        }
    }

    const buildValidations = (details: Partial<WorkDetailsModel>): IWorkDetailsValidations => {
        const { title, duration, languageCode, copyrightOfficeDate, rightsholderProprietaryId, iswc } = details
        return {
            title: {
                pipeFunction: workTitleInvalidCheck,
                error: !title
                    ? 'Required'
                    : !maxByteLength(200)(title)
                      ? `Invalid value, max length 200 bytes`
                      : containsNewLine(title)
                        ? 'May not include multiple lines'
                        : '',
            },
            duration: {
                isDuration: true,
                error:
                    !duration || duration.match(/^[0-9]{2}[:]{1}[0-9]{2}$/)
                        ? ''
                        : 'Enter the duration in the format 00:00',
            },
            languageCode: {
                isLanguage: true,
                error: !languageCode ? 'Please select language' : '',
            },
            copyrightOfficeNumber: { maxLength: 12 },
            copyrightOfficeDate: {
                isDate: true,
                error:
                    !copyrightOfficeDate || copyrightOfficeDate.match(DATE_REGEX)
                        ? ''
                        : 'Enter the date in format YYYY-MM-DD',
            },
            firstUseRefusalIndicator: { required: true },
            rightsholderProprietaryId: {
                maxLength: 14,
                error:
                    rightsholderProprietaryId && rightsholderProprietaryId.length > 14
                        ? `Invalid value, max length 14`
                        : '',
            },
            iswc: {
                isISWC: true,
                error: !iswc || ISWCValidator(iswc) ? '' : 'Invalid ISWC',
            },
        }
    }

    const originalWorkDetails = useMemo<Partial<WorkDetailsModel>>(() => ({ ...presetData() }), [])
    const [workDetails, setWorkDetails] = useState<Partial<WorkDetailsModel>>(() => ({ ...presetData() }))
    const [validation, setValidation] = useState<IWorkDetailsValidations>(() => buildValidations(workDetails))
    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)

    const getValues = (value: string | number | boolean | null, id: string) => {
        const data = getValueForm(value, id, workDetails, validation)
        setWorkDetails(data.formObject)
        setValidation(data.validationObject)
        setUpdatedWorkDetailsToDraft()
    }

    const setUpdatedWorkDetailsToDraft = () => {
        const workDraftFromLocal = workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return
        }
        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            ...workDetails,
            iswc: workDetails?.iswc ? workDetails.iswc.replace(/-/g, '').replace(/\./g, '') : null,
        }
        workStore.setWorkDraftToLocal(updatedWorkDraft)
        stepValidator()
    }

    const removeAlternativeTitle = (removingTitle: AlternativeTitleModel) => {
        const workDraftFromLocal = workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return
        }
        const updatedWorkDraft: WorkModel = akaTitleRemover(removingTitle, workDraftFromLocal)
        workStore.setWorkDraftToLocal(updatedWorkDraft)
        stepValidator()
    }

    const stepValidator = () => {
        const workDraftFromLocal = workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return
        }
        const invalid = workDetailsInvalidCheck(workDraftFromLocal, libStore.akaTitleTypes, libStore.languages)
        workStore.setWizardStepValidation(!invalid)
    }

    useEffect(() => {
        stepValidator()
    }, [])

    const languageOptions: OptionModel[] = libStore.languages
    const workDraftFromLocal = workStore.workDraftLocal

    if (!workDetails || !validation || !workDraftFromLocal || !languageOptions) {
        return null
    }

    const isSubmittedWork = !!(workDraftFromLocal.id && workDraftFromLocal.isComplete)

    return (
        <div className="work-details-step">
            <WorkDetailsFields
                workDetails={workDetails}
                validation={validation}
                languageOptions={languageOptions}
                originalWorkDetails={originalWorkDetails}
                isSubmittedWork={isSubmittedWork}
                workTitle={workDraftFromLocal.title}
                onChange={getValues}
            />

            <TablesForm
                className="alternative-titles-table"
                headingDecoration={{
                    icon: 'writers',
                    title: 'Alternative Titles',
                    required: false,
                    toolTip: <CustomToolTip inverse type="?" info={TOOLTIP_TEXT.createWork.alternativeTitles} />,
                }}
                tableInfos={AlternativeTitlesTableInfo()}
                addButtonTitle="Add Alternative Title"
                onAddButtonClicked={() => setSidebarIsOpen(true)}
                onRemoveCellClicked={(index: number) => {
                    removeAlternativeTitle(workDraftFromLocal.alternativeTitles[index])
                }}
                useAlternateHeader
            />

            <AddAlternativeTitlesSidebar open={sidebarIsOpen} onClose={() => setSidebarIsOpen(false)} />
        </div>
    )
}

export default observer(WorkDetailsStep)
